import React, { useState, useEffect } from 'react';
import { Image, Button, Flex, Box, Heading, Spinner, Alert, AlertIcon, Text } from '@chakra-ui/react';
import dominus_logo_transparent_white from '../assets/dominus_logo_transparent_white.png';
import { useLocation, useNavigate } from 'react-router-dom';

const SmsPolicyPage = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadDocument();
  }, []);

  const loadDocument = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch the HTML file directly from the public assets
      const response = await fetch('/system-docs/sms-policy.html');
      if (!response.ok) {
        throw new Error(`Failed to load document: ${response.statusText}`);
      }
      
      let htmlContent = await response.text();
      // Temporarily replace existing mailto links with placeholders
      const mailtoPlaceholders: string[] = [];
      htmlContent = htmlContent.replace(
        /<a href="mailto:([^"]+)">([^<]+)<\/a>/g,
        (match, href, text) => {
          const placeholder = `__MAILTO_PLACEHOLDER_${mailtoPlaceholders.length}__`;
          mailtoPlaceholders.push(match);
          return placeholder;
        }
      );
      
      // Wrap remaining email addresses in mailto links
      htmlContent = htmlContent.replace(
        /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
        '<a href="mailto:$1" style="color: #22a000; text-decoration: underline;">$1</a>'
      );
      
      // Restore the original mailto links
      mailtoPlaceholders.forEach((placeholder, index) => {
        htmlContent = htmlContent.replace(`__MAILTO_PLACEHOLDER_${index}__`, placeholder);
      });
      // Extract the body content from the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const bodyContent = doc.body.innerHTML;
      
      setContent(bodyContent);
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
    minHeight="100vh"
    w="100%"
    position="absolute"
    top="0"
    left="0"
    bgGradient="linear(to-b, black, #002e0b)"
    color="white"
    fontFamily="'Exo 2', sans-serif"
    display="flex"
    flexDirection="column"
  >
    {/* Navigation Bar */}
    <Box
      id="box1"
      as="nav"
      position="fixed"
      top={0}
      w="100%"
      bg="transparent"
      backdropFilter="blur(8px)"
      zIndex={50}
    >
      <Flex 
        justifyContent="space-between" 
        maxW="8xl" 
        mx="auto"
        px={4}
        py={4}
        alignItems="center"
      >
        <Box flexShrink={0}>
          <Image src={dominus_logo_transparent_white} alt="Dominus" h={12} />
        </Box>
        <Flex alignItems="center">
          <Button
            onClick={() => navigate('/signin')}
            px={8}
            py={2}
            visual="dominus"
          >
            Sign In
          </Button>
          <Button
            onClick={() => navigate('/signup')}
            px={8}
            py={2}
            visual="dominus"
            ml={4}
          >
            Register
          </Button>
        </Flex>
      </Flex>
    </Box>

    <Box mt={24} color="white" display="flex" alignItems="center" justifyContent="center">
      <Box p={10} maxW="800px" w="full" >
        <Heading as="h1" size="lg" mb={2} textAlign="center" fontFamily="Audiowide">
          SMS Policy
        </Heading>
        <Text fontSize="lg" mb={6} textAlign="center">
        for Dominus Baseball <br />
          <i>Dominus Baseball is a Mobile Global Esports product</i>
          </Text>
        
        {loading && (
          <Box display="flex" alignItems="center" >
            <Spinner size="xl" color="#22a000" />
          </Box>
        )}
        
        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}
        
        {!loading && !error && content && (
          <Box 
            dangerouslySetInnerHTML={{ __html: content }} 
            style={{
              paddingLeft: '10%',
              paddingRight: '10%',
              lineHeight: '1.6',
              fontSize: '14px',
              textAlign: 'left'
            }}
            sx={{
              'h1': {
                color: '#22a000',
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '20px',
                marginTop: '20px'
              },
              'h2': {
                color: '#22a000',
                fontSize: '20px',
                fontWeight: 'bold',
                marginBottom: '15px',
                marginTop: '20px'
              },
              'h3': {
                color: '#22a000',
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '10px',
                marginTop: '15px'
              },
              'p': {
                marginBottom: '10px'
              },
              'ul': {
                marginBottom: '15px',
                paddingLeft: '20px'
              },
              'li': {
                marginBottom: '5px'
              },
              'a': {
                color: '#22a000',
                textDecoration: 'underline',
                '&:hover': {
                  color: '#1a8000',
                  textDecoration: 'underline'
                }
              }
            }}
          />
        )}
      </Box>
    </Box>
    </Box>
  );
};

export default SmsPolicyPage; 